import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { shallowCompare, ReusableStreamProperty } from '@ipreo/muni-shared';
import { orderIsRetailType } from '../../gameday/data/gameDay-data-orders.calculation';
import { GameDayModel } from '../../models/gameday-model';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import { GameDayRootReducerState } from '../../state/root.state';
import {
  RetailData,
  getRetailReportsColumns
} from '../mappers/reports.calculations';
import {
  ManagerRetailReport,
  ManagersRetailReportWithTotals
} from '../models/manager';
import { getTrancheOrdersFilteredState } from '../../state/data/reducer';

@Injectable()
export class ManagersRetailService {
  @ReusableStreamProperty
  private readonly trancheFilteredOrders: Observable<GameDayCalculatedData> = this.store.pipe(
    select(getTrancheOrdersFilteredState),
    filter(({ orders }) => !!orders),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly getRetailOrdersData: Observable<
    ManagerRetailReport[]
  > = this.trancheFilteredOrders.pipe(
    map((data: GameDayCalculatedData) => {
      const result = data.orders.reduce(
        this.reduceRetailsOrdersData.bind(this),
        <{ [id: string]: ManagerRetailReport }>{}
      );

      data.deal.syndicate.forEach(
        ({ name, id, role }: GameDayModel.SyndicateMember) => {
          if (!result[id]) {
            result[id] = {
              total: 0,
              other: 0,
              lowPriority: 0,
              nocategory: 0,
              name: name,
              id,
              role
            };
          } else {
            result[id].name = name;
          }
        }
      );

      return Object.keys(result).map((key: string) => result[key]);
    })
  );

  @ReusableStreamProperty
  public readonly getColumns: Observable<string[]> = this.store.pipe(
    filter((state) => !!state.gameDay.data.deal),
    select(getTrancheOrdersFilteredState),
    distinctUntilChanged(),
    map(getRetailReportsColumns),
    map((data: RetailData) => data.headers),
    distinctUntilChanged(shallowCompare)
  );

  @ReusableStreamProperty
  public readonly getManagersRetailList: Observable<
    ManagerRetailReport[]
  > = combineLatest(this.getRetailOrdersData, this.getColumns).pipe(
    map(([managers, cols]: [ManagerRetailReport[], string[]]) => {
      cols.forEach((column) => {
        managers.forEach((manager) => {
          if (!manager[column]) {
            manager[column] = 0;
          }
        });
      });

      return managers;
    })
  );

  @ReusableStreamProperty
  public readonly getManagersRetailTotalList: Observable<ManagersRetailReportWithTotals> = combineLatest(
    [this.getManagersRetailList, this.getColumns]
  ).pipe(
    filter(([managers]) => managers.length > 0),
    map(([managers, cols]: [ManagerRetailReport[], string[]]) => {
      const columnsObj = cols.reduce(
        (prev, next: string) => {
          prev[next] = 0;
          return prev;
        },
        {
          total: 0,
          nocategoryTotal: 0,
          lowPriorityTotal: 0,
          otherTotal: 0
        }
      );

      // Total calculation for each generated column
      return managers.reduce(
        (prev: ManagersRetailReportWithTotals, next: ManagerRetailReport) => {
          prev.total += next.total;
          prev.nocategoryTotal += next.nocategory;
          prev.lowPriorityTotal += next.lowPriority;
          prev.otherTotal += next.other;

          const totalByCategories = cols.reduce<ManagersRetailReportWithTotals>(
            (prevCol: ManagersRetailReportWithTotals, nextCol: string) => {
              prev[nextCol] += next[nextCol];
              return prev;
            },
            {} as any
          );

          return { ...prev, ...totalByCategories };
        },
        columnsObj
      ) as ManagersRetailReportWithTotals;
    })
  );

  @ReusableStreamProperty
  public readonly isNoCategoryColumnPresent: Observable<boolean> = this.getManagersRetailTotalList.pipe(
    map(
      (totals: ManagersRetailReportWithTotals) =>
        !(
          totals.nocategoryTotal === 0 &&
          totals.lowPriorityTotal === 0 &&
          totals.otherTotal === 0
        )
    ),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly isColumnsPresent: Observable<boolean> = this.getColumns.pipe(
    map((columns: string[]) => columns.length > 0)
  );

  @ReusableStreamProperty
  public readonly noCategoryColumnHeaderLabel: Observable<string> = combineLatest(
    this.isNoCategoryColumnPresent,
    this.isColumnsPresent
  ).pipe(
    map(([isNoCategoryPresent, isColsPresent]: [boolean, boolean]) => {
      if (!isNoCategoryPresent) {
        return undefined;
      }
      if (isColsPresent) {
        return 'Retail - Other';
      }
      return 'Retail';
    }),
    distinctUntilChanged()
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  private reduceRetailsOrdersData(
    accumulator: { [id: number]: ManagerRetailReport },
    order: GameDayModel.Order
  ) {
    if (order.priority !== GameDayModel.OrderPriority.retail) {
      return accumulator;
    }

    let key: string;
    const { id, name, role } = order.syndicateDepartment;
    const entry =
      accumulator[id] ||
      (accumulator[id] = {
        total: 0,
        lowPriority: 0,
        nocategory: 0,
        other: 0,
        id,
        name,
        role
      });

    entry.total += +order.amount;

    if (order.category && order.category.name) {
      key = order.category.name;
      entry[key] = entry[key] || 0;
      entry[key] += +order.amount;
    } else {
      if (order.retailType === GameDayModel.RetailType.full) {
        entry.nocategory += +order.amount;
      }
      if (order.retailType === GameDayModel.RetailType.lowPriority) {
        entry.lowPriority += +order.amount;
      }
      if (orderIsRetailType(order)) {
        entry.other += +order.amount;
      }
    }

    return accumulator;
  }
}
